import "leaflet/dist/leaflet.css";

import { Box, Button, Card, Chip, Skeleton, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import { Globe } from "lucide-react";
import type { JSX } from "react";
import { useMemo } from "react";
import { CircleMarker, MapContainer, Popup, TileLayer, Tooltip } from "react-leaflet";

import type { SalesHarvestOrg } from "@xop/dashboard-shared";

import { SalesEmptyState, SalesErrorState } from "../components/SalesEmptyState.js";
import { salesApi } from "../client.js";
import { aggregateByCountry, type CountryAgg, markerRadius } from "./world-map/aggregate.js";

/** Pull the WHOLE pool (paginated) so the map shows every country, not just the
 * current page. Bounded at 50×100 = 5,000 orgs so a runaway pool can't hammer. */
async function fetchAllHarvestOrgs(vertical?: string): Promise<SalesHarvestOrg[]> {
  const perPage = 100;
  const all: SalesHarvestOrg[] = [];
  for (let page = 1; page <= 50; page += 1) {
    const res = await salesApi.getHarvestOrgs({
      ...(vertical ? { vertical } : {}),
      page,
      per_page: perPage,
    });
    all.push(...res.items);
    if (all.length >= res.total || res.items.length < perPage) break;
  }
  return all;
}

/**
 * The harvested pool on a world map — one dot per country at its centroid, sized
 * by how many contacts were dug there. Hover a dot to see which organisations
 * are there; click it for the full list (each opens the org). Reuses the same
 * react-leaflet stack as the CRM customer map.
 */
export function HarvesterWorldMap({
  vertical,
  onOpenOrg,
}: {
  vertical?: string;
  onOpenOrg: (orgId: string) => void;
}): JSX.Element {
  const theme = useTheme();
  const orgs = useQuery({
    queryKey: ["sales", "harvest", "orgs", "all", { vertical: vertical ?? "" }],
    queryFn: () => fetchAllHarvestOrgs(vertical || undefined),
    refetchInterval: 60_000,
  });

  const agg = useMemo(() => aggregateByCountry(orgs.data ?? []), [orgs.data]);

  if (orgs.isLoading && !orgs.data) {
    return <Skeleton variant="rounded" sx={{ width: "100%", height: 620, borderRadius: 1 }} />;
  }
  if (orgs.isError) {
    return <SalesErrorState error={orgs.error} />;
  }
  if (agg.countries.length === 0) {
    return (
      <SalesEmptyState
        title="Nothing to map yet"
        description="Once the harvester has dug contacts in a country, a dot appears here. Switch the agent to Contacts mode to fill the pool."
      />
    );
  }

  const fill = theme.palette.secondary.main;
  const stroke = theme.palette.primary.main;

  return (
    <Card variant="outlined" sx={{ overflow: "hidden" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Globe size={18} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Where the harvested contacts are
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip size="small" label={`${agg.countries.length} countries`} sx={{ borderRadius: 1 }} />
          <Chip size="small" label={`${agg.totalContacts.toLocaleString()} contacts`} sx={{ borderRadius: 1 }} />
          <Chip
            size="small"
            color="primary"
            variant="outlined"
            label={`${agg.totalOrgs.toLocaleString()} organisations`}
            sx={{ borderRadius: 1 }}
          />
        </Stack>
      </Box>

      {/* Map */}
      <Box sx={{ height: 620, width: "100%" }}>
        <MapContainer
          center={[25, 10]}
          zoom={2}
          minZoom={2}
          worldCopyJump
          style={{ height: "100%", width: "100%" }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          {agg.countries.map((c) => (
            <CircleMarker
              key={c.country}
              center={c.centroid}
              radius={markerRadius(c.contactCount)}
              pathOptions={{ color: stroke, weight: 1.5, fillColor: fill, fillOpacity: 0.78 }}
            >
              <Tooltip direction="top" opacity={1} sticky>
                <CountryTooltip c={c} />
              </Tooltip>
              <Popup minWidth={240} maxWidth={300}>
                <CountryPopup c={c} onOpenOrg={onOpenOrg} />
              </Popup>
            </CircleMarker>
          ))}
        </MapContainer>
      </Box>

      {/* Footnote */}
      <Box sx={{ px: 2, py: 1, borderTop: 1, borderColor: "divider" }}>
        <Typography variant="caption" color="text.secondary">
          Dot size scales with contacts dug. Hover a country to see who's there; click for the full list.
          {agg.unmappedOrgCount > 0 &&
            ` · ${agg.unmappedOrgCount} org${agg.unmappedOrgCount === 1 ? "" : "s"} not placed (unrecognised country).`}
        </Typography>
      </Box>
    </Card>
  );
}

/** Hover card: the "who's there" summary + the top organisations by contacts. */
function CountryTooltip({ c }: { c: CountryAgg }): JSX.Element {
  const top = [...c.orgs].sort((a, b) => (b.contact_count || 0) - (a.contact_count || 0)).slice(0, 6);
  const more = c.orgCount - top.length;
  return (
    <Box sx={{ minWidth: 180, maxWidth: 280 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{c.country}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
        {c.orgCount} organisation{c.orgCount === 1 ? "" : "s"} · {c.contactCount} contact{c.contactCount === 1 ? "" : "s"}
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 2 }}>
        {top.map((o) => (
          <Box component="li" key={o.id} sx={{ fontSize: 12.5, lineHeight: 1.5 }}>
            {o.name}
            {o.contact_count ? ` (${o.contact_count})` : ""}
          </Box>
        ))}
      </Box>
      {more > 0 && (
        <Typography variant="caption" color="text.secondary">+{more} more — click to see all</Typography>
      )}
    </Box>
  );
}

/** Click card: every org in the country, each opens its dialog. */
function CountryPopup({ c, onOpenOrg }: { c: CountryAgg; onOpenOrg: (id: string) => void }): JSX.Element {
  const sorted = [...c.orgs].sort((a, b) => (b.contact_count || 0) - (a.contact_count || 0));
  return (
    <Box sx={{ minWidth: 220 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{c.country}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
        {c.orgCount} organisations · {c.contactCount} contacts
      </Typography>
      <Stack spacing={0.25} sx={{ maxHeight: 220, overflowY: "auto" }}>
        {sorted.map((o) => (
          <Button
            key={o.id}
            size="small"
            onClick={() => onOpenOrg(o.id)}
            sx={{ justifyContent: "space-between", textTransform: "none", textAlign: "left", px: 1, py: 0.5 }}
            fullWidth
          >
            <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {o.name}
            </Box>
            <Box component="span" sx={{ ml: 1, color: "text.secondary", fontVariantNumeric: "tabular-nums" }}>
              {o.contact_count || 0}
            </Box>
          </Button>
        ))}
      </Stack>
    </Box>
  );
}
